import { isMeetingSessionRoleGreaterThanOrEqual, isSpectator, MeetingOrderedMedia, MeetingOrderedSession, useOrderedMeetingMedia, useOrderedMeetingSessions } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { SessionMode } from "../../contexts/appStore/hooks";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { CarouselData } from "../../helpers/bridge/common";
import { debounce } from "lodash";
import { useSettings } from "../settings";
import { SecondaryScreenLayout } from "../../interfaces/secondaryScreens";
import { ScreenConstants } from "../../constants/screens";
import { ScreenHelpers } from "../../helpers/screen";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

const roundSplit = <T> (
  items: T[], 
  screens: { capacity: number, rounds: { enabled: boolean, items: T[] }[] }[],
) => {
  let currRoundIndex = 0;
  let currScreenIndex = 0;

  const pushItem = (item: T): boolean => {
    const currScreen = screens[currScreenIndex];
    let currRound = currScreen.rounds.length > currRoundIndex ? currScreen.rounds[currRoundIndex] : undefined;
    if (currRound === undefined) {
      currRound = { enabled: true, items: [] };
      currScreen.rounds.push(currRound);
    } 
    if (currRound.items.length >= currScreen.capacity) {
      if (currScreenIndex + 1 >= screens.length) {
        currScreenIndex = 0;
        currRoundIndex ++;
        return pushItem(item);;
      }
      currScreenIndex++;
      return pushItem(item);
    }
    currRound.items.push(item);
    return true;
  }

  items.forEach((i) => {
    pushItem(i);
  });
};

interface VideoCarouselData {
  media: MeetingOrderedMedia[],
  displayNextCarousel?: Date,
  displayNextCarouselTimeout?: NodeJS.Timer,
  /*
    Here screens designates unique video groups,
    so it will have to be renamed videoGroups

    And then, the screen will be served the current round of the
    video group they specified

    Each screen, depending on the video area it has or the number of it
    will have a max capacity, so there's that to take into account
  */
  groups: {
    id: number, 
    capacity: number, // has to be the smallest of all screens using this videoGroup
    roundIndex: number, 
    nextRoundIndex?: number, 
    rounds: { 
      enabled: boolean, 
      items: MeetingOrderedMedia[],
    }[],
  }[],
}

export const useVideoCarousel = ({
  meeting,
  session,
  getClients,
  onCarousel,
}: {
  meeting?: Meeting,
  session?: MeetingSession,
  getClients: () => MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[],
  onCarousel: (data: CarouselData, delay: number, page: number, pageCount: number, id: string) => void,
}) => {
  const [settings] = useSettings();

  const orderedMedia = useOrderedMeetingMedia(meeting, useCallback((_, s) => {
    if (!session) return false;
    // TODO : we could put the filtering condition in settings
    // For example : only send admins on in the carousel
    //if (isMeetingSessionRoleGreaterThanOrEqual(s.role, MeetingSessionRole.ADMINISTRATOR)) return false;
    if (isSpectator(s.role)) return false;
    return s.id !== session?.id;
  }, [session]));
  const orderedMediaRef = useRefAutoUpdate(orderedMedia);

  const connectedOrderedMedia = useMemo(() => orderedMedia.filter(m => m.connected), [orderedMedia]);
  const connectedOrderedMediaRef = useRefAutoUpdate(connectedOrderedMedia);
  
  const carouselIntervalRef = useRef<NodeJS.Timer>();
  const carouselInfoRef = useRef<VideoCarouselData>();

  const getLayoutPlayersCapacity = (layout: SecondaryScreenLayout) => {
    return layout.columns.reduce(
      (acc, column) => {
        if (column.contentType !== "players") return acc;
        const { columns, rows } = ScreenHelpers.getColumnPlayers(column);
        return acc + columns * rows;
      }, 
      0,
    );
  }

  const layoutsCapacities = useMemo( 
    () => Object.fromEntries( 
      (settings.carousel?.layouts ?? [])?.map(layout => ([ 
        layout.id, 
        getLayoutPlayersCapacity(layout),
      ])),
    ), 
    [settings], 
  ); 

  const getClientCapacity = useCallback((client: MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>) => {
    const layoutId = client.data?.data?.layoutId;
    let capacity: number | undefined = undefined;
    if (layoutId !== undefined) {
      capacity = layoutsCapacities[layoutId];
    }
    if (capacity === undefined) {
      // Either layoutId is undefined, or does not match any layout
      // Maybe config provides a default layout :
      let defaultLayout = settings?.carousel?.layouts?.find(l => l.id === undefined);
      if (!defaultLayout) defaultLayout = ScreenConstants.DefaultLayout;
      capacity = getLayoutPlayersCapacity(defaultLayout);
    }
    return capacity;
  }, [layoutsCapacities]);

  const convertToPageInfo = (idx: number, rounds: VideoCarouselData['groups'][number]['rounds']): {
    page: number, 
    pageCount: number,
    round?: VideoCarouselData['groups'][number]['rounds'][number],
  } => {
    let page = -1;
    let pageCount = 0;
    let selectedRound: VideoCarouselData['groups'][number]['rounds'][number] | undefined = undefined;

    // we'll try to get back on the index we were before,
    // if not possible, we go to the next available page after this index,
    // if there is none, we loop back to the first available page
    //
    // if page are 0,1,0,[0],1
    // then we'll get 0,1,0,0,[1]
    //
    // if page are 0,1,0,[0],0
    // then we'll get 0,[1],0,0,0
    rounds.forEach((round, roundIdx) => {
      if (round.enabled) {
        if (roundIdx < idx) {
          // we are under ideal index, we continue going
          page++;
        } else {
          // we are at ideal index or above
          if (!selectedRound) {
            // now, we need to pick the first enabled round after the ideal index
            selectedRound = round;
            page++;
          }
        }
        pageCount++;
      }
    });
    
    return { page: selectedRound ? page : 0, pageCount, round: selectedRound };
  };

  /**
   * Clients belonging to the same video group will receive the same
   * sessions
   * 
   * Sessions are split between the different video groups, then into rounds
   */
  const getClientMappedByVideoGroup = (): Map<number, MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[]>  => {
    const clients = getClients();
    const map = new Map<number, MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[]>();
    clients.forEach((c) => {
      const capacity = getClientCapacity(c);
      if (capacity < 1) return; // cannot display anything, does not contain "players" columns

      const videoGroupId = c.data?.data?.videoGroupId ?? 0;
      let curr = map.get(videoGroupId);
      if (!curr) curr = [];
      curr.push(c);

      map.set(videoGroupId, curr);
    });
    return map;
  }

  const generateCarouselInfo = () => {
    const media = orderedMediaRef.current;
    const previousCarouselInfo = carouselInfoRef.current;
    const newCarouselData: VideoCarouselData = { 
      media: media,
      displayNextCarousel: previousCarouselInfo?.displayNextCarousel,
      displayNextCarouselTimeout: previousCarouselInfo?.displayNextCarouselTimeout,
      groups: []
    };
    carouselInfoRef.current = newCarouselData;

    // Video Groups
    // ---->
    const groupedByVideoGroups = getClientMappedByVideoGroup();

    const videoGroups: number[] = Array.from(groupedByVideoGroups.keys())
      .map((k) => Number(k))
      .filter((n) => !isNaN(n))
      // we want stream to unwind on screens this way :
      // 3, 4, 5, ..., 1, 2
      // because the 3+ are the middle screens

      // will be solved now that we can just put a videoGroupId,
      // just put the ID in the priority order, higher first
      // .sort((a, b) => {
      //   // priority to screen > 3
      //   if ((a === 1 || a === 2) && (b === 1 || b === 2)) return a - b;
      //   if (a === 1 || a === 2) return 1;
      //   if (b === 1 || b === 2) return -1;
      //   return a - b;
      // });
      .sort();

    videoGroups.forEach((s) => {
      const currClients = groupedByVideoGroups.get(s) ?? [];
      let capacity = 0;
      currClients.forEach((client) => {
        const clientCapacity = getClientCapacity(client);
        if (clientCapacity) {
          capacity = Math.max(capacity, clientCapacity);
        }
      });
      const previousScreen = previousCarouselInfo?.groups?.find((screen) => screen.id === s);

      // Session Mode
      const roundIndex = previousScreen?.roundIndex ?? 0;
      const nextRoundIndex = previousScreen?.nextRoundIndex;
      
      // we create the empty screens
      // with capacities depending on the clients
      newCarouselData.groups.push({
        id: s,
        capacity,
        
        roundIndex,
        nextRoundIndex,
        rounds: [],
      })
    });
    // <----
    
    if (newCarouselData.groups.length < 1) return;

    // Sessions
    // we fill the screens
    roundSplit(media, newCarouselData.groups.map(
      s => ({ 
        capacity: s.capacity, 
        rounds: s.rounds,
      })
    ));
  
    // remove rounds if only disconnect user & round > 1
    newCarouselData.groups?.forEach((s) => {
      s.rounds.forEach((round) => {
        let containsOnlyDisconnectedUsers = true;
        for (let session of round.items) {
          if (session.connected) {
            containsOnlyDisconnectedUsers = false;
            break;
          }
        }
        if (containsOnlyDisconnectedUsers) {
          round.enabled = false;
        }
      });

      s.roundIndex = getValidRoundIndex(s, s.roundIndex);
      if (s.nextRoundIndex) s.nextRoundIndex = getValidRoundIndex(s, s.nextRoundIndex);
    });
  };

  const getValidRoundIndex = (screen: VideoCarouselData['groups'][number], startedAt: number) => {
    if (!screen.rounds.length) return 0;
    let currIndex = startedAt % screen.rounds.length;
    const firstIndex = currIndex;
    let isValid = false;
    do {
      const round = screen.rounds[currIndex];
      if (round.enabled) {
        isValid = true;
        break;
      }
      currIndex = (currIndex + 1) % screen.rounds.length
    } while (currIndex !== firstIndex);
    return isValid ? currIndex : 0;
  }

  const update = useCallback(debounce(() => {
    generateCarouselInfo();
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const clients = getClientMappedByVideoGroup();

    carouselInfo.groups.forEach((group) => {
      const currClients = clients.get(group.id);

      // Session Mode
      if (!group.rounds.length) {
        currClients?.forEach((client) => {
          onCarousel({ medias: [], totalSessions: 0 }, 0, 0, 0, client.id);
        });
        return;
      }
      // Setting current round
      group.roundIndex = getValidRoundIndex(group, group.roundIndex);

      currClients?.forEach((client) => {
        // Session Mode
        const clientCapacity = getClientCapacity(client);
        const currCapacity = Math.min(group.capacity, clientCapacity);
        const pageInfo = convertToPageInfo(group.roundIndex, group.rounds);
        const data: CarouselData = {
          medias: pageInfo.round ? pageInfo.round.items.slice(0, currCapacity).map((s) => s.media) : [],
          totalSessions: connectedOrderedMediaRef.current.length,
        };
        onCarousel(data, 0, pageInfo.page, pageInfo.pageCount, client.id);
      });
    });
    
    /**
     * 
     * Bug explaination when receiving a lot of fast updates :
     * - setNextCarousel was called from the main setInterval with page=N+1 (next page)
     * - then right away, here postDisplayCarousel was called with delay=0, forcing slaves to update back to page=N
     *   -> roundIndex still pointing to page N, because the roundIndex=N+1 is done at the end of the setNextCarousel timeout
     * - then here setNextCarousel was not called again because of a condition that delay must be > 0
     * 
     * Fix :
     * - here, setNextCarousel is now called again, with delay=0, immediatly setting the next page again
     */

    // refresh next carousel if already scheduled
    if (carouselInfo.displayNextCarousel) {
      const now = new Date();
      const delay = carouselInfo.displayNextCarousel.getTime() - now.getTime();
      setNext("increment", Math.max(0, delay));
    }
  }, 500, { leading: true, maxWait: 500 }), []);
  
  const setNext = (
    mode: 'increment' | 'restart', 
    delay: number = 0,
  ) => {
    const carouselInfo = carouselInfoRef.current;
    if (!carouselInfo) return;

    const clients = getClientMappedByVideoGroup();

    carouselInfo.groups.forEach((screen) => {
      const currClients = clients.get(screen.id);

      // Session Mode
      if (!screen.rounds.length) {
        currClients?.forEach((c) => {
          onCarousel({ medias: [], totalSessions: 0 }, delay, 0, 0, c.id);
        });
        return;
      }
      let nextRoundIndex = getValidRoundIndex(screen, screen.roundIndex);
      if (mode === "restart") nextRoundIndex =  getValidRoundIndex(screen, 0);
      else if (mode === 'increment') nextRoundIndex = getValidRoundIndex(screen, screen.roundIndex + 1);
      // Setting next round
      screen.nextRoundIndex = nextRoundIndex;

      currClients?.forEach((client) => {
        // Session Mode
        const clientCapacity = getClientCapacity(client);
        const pageInfo = convertToPageInfo(screen.nextRoundIndex ?? 0, screen.rounds);
        const data: CarouselData = {
          medias: pageInfo.round ? pageInfo.round.items.slice(0, clientCapacity).map((s) => s.media) : [],
          totalSessions: connectedOrderedMediaRef.current.length,
        };  
        onCarousel(data, delay, pageInfo.page, pageInfo.pageCount, client.id);
      })
    });

    if (carouselInfo.displayNextCarouselTimeout) clearTimeout(carouselInfo.displayNextCarouselTimeout);

    // saved date of next carousel will be displayed
    const updateIndexes = () => {
      // update local indexes
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.groups.forEach((s) => {
        // next carousel displayed
        if(s.nextRoundIndex !== undefined) s.roundIndex = s.nextRoundIndex;
        s.nextRoundIndex = undefined;
      });
    };
    
    if (delay > 0) {
      carouselInfo.displayNextCarousel = new Date(new Date().getTime() + delay);
      // update indexes after delay
      carouselInfo.displayNextCarouselTimeout = setTimeout(updateIndexes, delay);
    } else {
      // update indexes now
      updateIndexes();
      carouselInfo.displayNextCarousel = undefined;
      carouselInfo.displayNextCarouselTimeout = undefined;
    }
  }
  
  const stop = () => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    if (carouselInfoRef.current?.displayNextCarouselTimeout) clearTimeout(carouselInfoRef.current.displayNextCarouselTimeout);
    carouselIntervalRef.current = undefined;
    carouselInfoRef.current = undefined;
  };
  
  const start = () => {
    stop();
    generateCarouselInfo();
    setNext('restart', 0);
    carouselIntervalRef.current = setInterval(() => {
      setNext('increment', settings?.carousel?.prelaodDelay ?? DEFAULT_CAROUSEL_DELAY);
    }, settings?.carousel?.interval ?? DEFAULT_CAROUSEL_INTERVAL);
  };

  const isRunning = () => {
    return carouselIntervalRef.current !== undefined;
  };

  return {
    update,
    start,
    stop,
    isRunning,
  };

}